import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import BarraInferiorBotoes from '../../../../../components/BarraInferiorBotoes';
import BarraSubAbas from '../../../../../components/BarraSubAbas';
import ContainerConteudo from '../../../../../components/ContainerConteudo';
import { type CustomTableHeader } from '../../../../../components/CustomTable';
import DetailsTable from '../../../../../components/CustomTable/DetailsTable';
import CustomTableOffcanvas from '../../../../../components/CustomTableOffcanvas';
import WorkspaceBotoes from '../../../../../components/WorkspaceBotoes';
import { getUser } from '../../../../../modules/contexts/Auth/RequireAuth';
import { IModuloRegistro } from '../../../../../modules/types/ModuloSisaf';
import { getAllModulos } from '../../../../../services/modulosSisaf/moduloService';
import theme from '../../../../../theme';
import { useExcelExport } from '../../../../../utils/excel-export';
import {
  formataNomeMeioSiglas,
  formatDate,
  formatDateToFile,
} from '../../../../../utils/functions';
import { renderStatusDot } from '../../../ProdutosDAF/Produtos/auxiliarProdutosDaf';
import { optionStatusBin } from '../../Dominios/utils/optionStatusBin';
import { getSubAbas } from '../../subabas';

type ModuloTableRow = {
  id: number;
  status: React.ReactNode;
  modulo: string;
  descricao: string;
  submodulos: React.ReactNode;
};

const headersTable: CustomTableHeader<ModuloTableRow>[] = [
  { text: 'ID', key: 'id', width: 6 },
  {
    text: 'Status',
    key: 'status',
    width: 5,
    fieldType: 'checkbox',
    renderCell: (value) => renderStatusDot(value),
  },
  { text: 'Módulo', key: 'modulo', width: 22 },
  { text: 'Descrição', key: 'descricao', width: 32 },
  { text: 'Submódulos', key: 'submodulos', width: 32 },
];

const valoresOffcanvas: Record<string, string> = {
  id: 'ID',
  dataRegistro: 'Registro',
  dataUltimaAtualizacao: 'Última Atualização',
  dataUltimaVisualizacao: 'Última Visualização',
  nEdicoes: 'Edições',
  nVisualizacoes: 'Visualizações',
  nVisualizadores: 'Visualizadores',

  separadorLog: '',
  separadorInterfaces: '',
  separadorDados: '',

  status: 'Status',
  interfaceDaf: 'Interface DAF',
  interfaceSes: 'Interface SES',
  interfaceFornecedor: 'Interface Fornecedor',
  modulo: 'Módulo',
  descricao: 'Descrição',
  observacoes: 'Observações',
  submodulos: 'Submódulos',
};

const statusBadge = (status: number) => (
  <span
    style={{
      backgroundColor:
        status === 2 ? theme.colors.verdegov : theme.colors.vermelho,
      borderRadius: '10px',
      padding: '10px',
      display: 'inline-block',
      minWidth: '70px',
      textAlign: 'center',
      color: 'white',
      fontWeight: 600,
    }}
  >
    {optionStatusBin.find((opt) => opt.value === status)?.label || 'N/I'}
  </span>
);

const flagBadge = (flag?: string) => (
  <span
    style={{
      backgroundColor:
        flag === 'S' ? theme.colors.verdegov : theme.colors.botaoAcao.cinza,
      color: flag === 'S' ? theme.colors.white : theme.colors.black,
      borderRadius: '8px',
      padding: '8px 10px',
      display: 'inline-block',
      minWidth: '80px',
      textAlign: 'center',
      fontWeight: 600,
    }}
  >
    {flag === 'S' ? 'Sim' : 'Não'}
  </span>
);

const ModulosSisafListagem: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const userID = user.sub;

  const [data, setData] = useState<IModuloRegistro[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const fetchData = async () => {
    try {
      const registros = await getAllModulos();
      setData(registros);
    } catch (error) {
      console.error('Erro ao buscar módulos:', error);
      setData([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const tableData: ModuloTableRow[] = useMemo(
    () =>
      data.map((item) => ({
        id: item.id,
        status: item.status === 1,
        modulo: item.modulo,
        descricao: item.descricao || '—',
        submodulos: item.submodulos || '-',
      })),
    [data],
  );

  const offCanvasData = useMemo(
    () =>
      data.map((item) => ({
        id: item.id,
        dataRegistro:
          formatDate(item.dataRegistro) +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioRegistro.nomeCompleto),
        dataUltimaAtualizacao:
          formatDate(item.dataUltimaAtualizacao) +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioAtualizacao.nomeCompleto),
        dataUltimaVisualizacao:
          formatDate(item.dataUltimaVisualizacao) +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioUltimaVisualizacao.nomeCompleto),
        nEdicoes: item.nEdicoes,
        nVisualizacoes: item.nVisualizacoes,
        nVisualizadores: item.nVisualizadores,

        separadorLog: '',
        status: statusBadge(item.status),

        separadorInterfaces: '',
        interfaceDaf: flagBadge(item.interfaceDaf),
        interfaceSes: flagBadge(item.interfaceSes),
        interfaceFornecedor: flagBadge(item.interfaceFornecedor),

        separadorDados: '',
        modulo: item.modulo,
        descricao: item.descricao || 'Não informado',
        observacoes: item.observacoes || 'Não informado',
        submodulos: item.submodulos || '-',
      })),
    [data],
  );

  const offcanvasOpenRoute = useMemo(() => {
    if (typeof window === 'undefined' || !selectedId) return undefined;
    const fichaPath = `/modulo/${selectedId}/dadosgerais`;
    return `${window.location.origin}/#${location.pathname}${fichaPath}`;
  }, [location.pathname, selectedId]);

  const handleRowClick = (row: ModuloTableRow) => {
    if (!row?.id) return;
    navigate(`modulo/${row.id}/dadosgerais`);
  };

  const handleOpenDetails = (row: ModuloTableRow) => {
    if (!row?.id) return;
    setSelectedId(row.id);
  };

  const handleClickInserir = () => {
    navigate('modulo/dadosgerais');
  };

  const { exportData, isExporting } = useExcelExport('listagemmodulossisaf');
  const handleExport = async () => {
    try {
      await exportData(
        data,
        `sisaf_modulos_${formatDateToFile(new Date().toISOString())}.xlsx`,
        userID,
        'Listagem de Módulos',
        'DAF',
      );
    } catch (error) {
      console.error('Erro na exportação:', error);
    }
  };

  return (
    <>
      <BarraSubAbas subAbas={getSubAbas()} />

      <ContainerConteudo>
        <CustomTableOffcanvas
          headers={headersTable}
          data={tableData}
          displayValues={valoresOffcanvas}
          offcanvasTitle="Módulo"
          widthOffcanvas="60%"
          openRoute={offcanvasOpenRoute}
          onOpenOffcanvas={handleOpenDetails}
          onRowClick={handleRowClick}
          itemsPerPage={50}
          showPagination
          showOpenButton
          filterable
          sortable
          minHeight="550px"
          data-testid="modulos-sisaf-table"
        >
          {(row) => (
            <DetailsTable
              rows={offCanvasData}
              selectedId={row?.id ?? selectedId}
              displayValues={valoresOffcanvas}
            />
          )}
        </CustomTableOffcanvas>
      </ContainerConteudo>

      <BarraInferiorBotoes>
        <WorkspaceBotoes
          inserir
          onClickInserir={handleClickInserir}
          download
          downloadDisabled={isExporting}
          onClickDownload={handleExport}
        />
      </BarraInferiorBotoes>
    </>
  );
};

export default ModulosSisafListagem;
